import React from "react";
import { Lock } from "lucide-react";

const AUDITS = [
  { code: "AU_01", title: "Contribution-Margin Waterfall", lede: "Every dollar of gross sales traced through returns, COGS, fulfilment and acquisition to a real net figure. Then the three reads on it: which single cost line is actually binding, what the fixed base demands in revenue, and the blended return on ad spend your own margin needs before paid acquisition washes its face." },
  { code: "AU_02", title: "Every Finding, Sized and Sequenced", lede: "What each leak is worth per month, ordered by what pays most per week of work rather than by how alarming it sounds, with a stated action for each." },
  { code: "AU_03", title: "Competitor and Category Position", lede: "Your pricing, discounting and stock discipline against a named competitor's live catalogue read the same day, plus whatever the Australian and New Zealand category actually publishes - and a straight answer where it publishes nothing." },
  { code: "AU_04", title: "Scenario Testing, With the Volume Risk Priced", lede: "What a real change in price, discount depth, spend, ROAS or return rate does to your bottom line - and for anything that moves price, the unit-volume loss at which the move stops paying. We do not borrow an elasticity from someone else's customers." },
];

const LOREM_ROWS = [
  ["\u2588\u2588.\u2588%", "\u2588\u2588.\u2588%", "\u2588,\u2588\u2588\u2588 bps", "requires P&L"],
  ["\u2588\u2588.\u2588%", "\u2588\u2588.\u2588%", "\u2588,\u2588\u2588\u2588 bps", "requires P&L"],
  ["\u2588\u2588.\u2588%", "\u2588\u2588.\u2588%", "\u2588\u2588\u2588 bps", "requires P&L"],
  ["\u2588\u2588.\u2588%", "\u2588\u2588.\u2588%", "\u2588,\u2588\u2588\u2588 bps", "requires P&L"],
];

export default function LockedAudits({ onUnlock }) {
  return (
    <section id="locked" className="border-t border-[#c9c3b8] bg-[#dcd7ce]/60">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="max-w-3xl mb-14">
          <div className="tick mb-4">04 · Locked advanced audits</div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-[#171717] leading-[1.05] mb-5">
            Four audits that need your P&L.
          </h2>
          <p className="text-[#404040] font-mono-tech text-[13px] leading-relaxed max-w-xl">
            The free sweep stops at what public feeds can show — nothing about your
            actual cost structure. These four need the numbers only you have, and
            unlock the moment you commission the Margin Audit.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 stagger-children">
          {AUDITS.map((a, i) => (
            <article key={a.code} data-testid={`locked-audit-${i}`} className="relative card-cream card-lift overflow-hidden flex flex-col">
              {/* Preview region — glass-veil is scoped to just this wrapper, not
                  the whole card, so the bottom-aligned title/lede can't crowd
                  the footer bar below it. */}
              <div className="relative flex-1">
                <div className="locked-blur p-8">
                  <div className="tick mb-4">Preview layer</div>
                  <table className="w-full font-mono-tech text-[12px] text-[#404040]">
                    <thead>
                      <tr className="text-left border-b border-[#ddd8ce]">
                        <th className="py-2 pr-4 tick">Claimed</th>
                        <th className="py-2 pr-4 tick">Adjusted</th>
                        <th className="py-2 pr-4 tick">Leak</th>
                        <th className="py-2 pr-4 tick">Signal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {LOREM_ROWS.map((r, j) => (
                        <tr key={j} className="border-b border-[#ede9e1]">
                          {r.map((c, k) => <td key={k} className="py-3 pr-4">{c}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="glass-veil absolute inset-0 z-10 flex items-end justify-between gap-4 p-6 pb-7 pointer-events-none">
                  <div>
                    <div className="tick mb-2">Locked</div>
                    <h3 className="font-serif-display text-2xl text-[#171717] leading-tight max-w-sm">{a.title}</h3>
                    <p className="font-mono-tech text-[12px] text-[#404040] mt-3 max-w-md">{a.lede}</p>
                  </div>
                  <div className="flex items-center justify-center w-12 h-12 border border-[#c9c3b8] bg-[#f5f2ed] shrink-0">
                    <Lock size={16} className="text-[#9a3412]" />
                  </div>
                </div>
              </div>

              <div className="relative z-20 border-t border-[#c9c3b8] p-4 flex items-center justify-between bg-[#faf7f2]">
                <span className="inline-block font-mono-tech text-[10px] tracking-widest uppercase px-2 py-1 rounded-full bg-[#171717] text-[#e88a51]">
                  Paid · Margin Audit
                </span>
                <button onClick={onUnlock} className="btn-secondary" data-testid={`locked-audit-cta-${i}`}>Unlock</button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
